import uniq from 'lodash/uniq';
import { sortLexicographically } from '../../lib/sortLexicographically';
import type {
  SalesforceItem,
  SalesforceItemField,
  SalesforceItemRollup,
  SalesforceItemSweepAlert,
  SalesforceItemSweepAssignment,
  SalesforceItemSweepAutomation,
  SalesforceItemSweepDedupe,
  SalesforceItemSweepMatching,
  SalesforceItemSweepScheduledAssignment,
} from '../parser/ParserTypes';
import {
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_AUTOMATION,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
  SWEEP_SCHEDULED_ASSIGNMENT,
  ROLLUP,
} from '../parser/ParserTypes';
import flatten from 'lodash/flatten';
import { ConfigurationToName, UnclassifiedTypes } from './salesforce-documentation-card/types';
import { ANY_STEP_NAME, getDisplayLineTexts } from '../Automations/helper';
import { getDataFromSFFieldName } from '../pages/rollups/rollupHelpers';
import { colors } from '@sweep-io/sweep-design';
import type { Tag } from '../../apis/facades/useUserInputsApiFacade';
import { telemetry } from '../../telemetry';
import { AutomationType } from '../../types/enums/AutomationType';
import type { UserInfo } from '../../types/UserInfoTypes';
import pluralize from 'pluralize';
import { getObjectTypeColor } from '../sweep-canvas/helpers/getObjectTypeColor';
import { FunnelFieldLabels } from '../../constants/deprecated_fieldsManagementConsts';
import { formatCamelCase } from './universal-search/utils';
import type { SweepFieldSummary } from '../../types/fields';
import { uniquePrimitives } from '../../lib/uniqueByKey';
import { AutomationTypes } from '../../types/enums/AutomationTypes';
import { createRecordTypeIdentifier } from './pills/pillsUtils';
import { FIELD } from '@server/parser';

export const getObjectApiNamesFromFunnelMap = (funnelsData?: FunnelsData) => {
  const funnels = Object.values(funnelsData ?? {});

  return uniq(
    sortLexicographically({
      items: funnels?.map((funnel) => funnel.recordType?.objectName) ?? [],
    }),
  );
};

export const getObjectApiNamesFromRecordTypes = (recordTypesData?: RecordTypesData) => {
  return flatten(Object.keys(recordTypesData ?? {}).map((key) => key.split('.')[0]));
};

export const displayCorrectDateFormat = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW = '250px';
export const OCCUPIED_SPACE_MAIN_VIEW = '200px';
export const OCCUPIED_SPACE_SINGLE_OBJECT_VIEW_CANVAS = '300px';

export const mapMissingInternalIdToMetadataId = (item: any) => ({
  ...item,
  id: item.id === 'MissingInternalId' || !item.id ? item.metadataId : item.id,
});

export const isItemActive = (item: any) =>
  item?.hasOwnProperty('isActive') ? item.isActive : true;

export const checkIfActive = (onlyActive: boolean, item: any) =>
  !onlyActive || (onlyActive && isItemActive(item));

export const translateTypeToHumanName = (type: string) => {
  if (!type) {
    return type;
  }
  return (
    ConfigurationToName[type] ?? FunnelFieldLabels[type as SweepFieldTypes] ?? formatCamelCase(type)
  );
};

export const automationTypeToConfigurationType = (type: AutomationType) => {
  switch (type) {
    case AutomationType.Alert:
      return SWEEP_ALERT;
    case AutomationType.Assignment:
      return SWEEP_ASSIGNMENT;
    case AutomationType.Dedupe:
      return SWEEP_DEDUPE;
    case AutomationType.Matching:
      return SWEEP_MATCHING;
    case AutomationType.ScheduledAssignment:
      return SWEEP_SCHEDULED_ASSIGNMENT;
    case AutomationType.Default:
      return SWEEP_AUTOMATION;

    case AutomationType.ScheduledReport:
    case AutomationType.ScheduledJob:
      //no documentation support yet
      break;

    default:
      telemetry.captureError(new Error('unknown type in automationTypeToConfigurationType'), {
        type,
      });
      break;
  }
};

export const createConfigurationItemFromTag = (tag: Tag): SalesforceItem => ({
  id: tag.id,
  name: tag.name,
  label: tag.name,

  objectApiNames: [],
  type: UnclassifiedTypes.tag,
  isActive: true,
  parentType: UnclassifiedTypes.tag,
  metadataId: '',
});

export const createConfigurationItemFromSweepField = (
  sweepField: SweepFieldSummary,
): SalesforceItemField => ({
  id: sweepField.id ?? '',
  name: getDataFromSFFieldName(sweepField?.sfFieldName)?.[1] ?? '',
  label: sweepField.sweepFieldName ?? '',

  objectApiNames: [sweepField.objectName],
  type: sweepField.fieldType,
  fieldType: sweepField.fieldType,
  isActive: true,
  parentType: FIELD,
  metadataId: '',
});

export const createConfigurationItemFromRollupField = ({
  id,
  sweepField,
  usage,
  link,
}: {
  id: string;
  sweepField: SweepField;
  usage?: number;
  link?: string;
}): SalesforceItemRollup => ({
  id,
  name: getDataFromSFFieldName(sweepField?.sfFieldName)?.[1] ?? '',
  label: sweepField.sweepFieldName ?? '',

  objectApiNames: [sweepField.objectName],
  type: sweepField.fieldType,
  description: sweepField.description,
  createdAt: displayCorrectDateFormat(sweepField?.createdAt),
  createdBy: sweepField?.createdBy ?? '',
  updatedAt: displayCorrectDateFormat(sweepField?.updatedAt),
  updatedBy: sweepField?.updatedBy ?? '',
  helpText: sweepField.helpText,
  isMandatory: sweepField.isRequired,
  formula: sweepField.properties.formula,
  usage,
  link,
  isActive: true,
  isRollup: true,
  parentType: ROLLUP,
  metadataId: id,
  fieldType: sweepField.fieldType,
});

export const getRecordTypeNamesFromAutomation = (automation?: AutomationStructureNew) => {
  if (!automation) {
    return [];
  }

  const funnelRecordTypeName = automation.funnelRecordType?.name
    ? createRecordTypeIdentifier({
        recordTypeName: automation.funnelRecordType?.name ?? '',
        funnelId: automation.automationDetails?.funnelId ?? '',
        objectName: automation.objectName,
      })
    : '';

  const rtNames =
    automation.automationDetails?.recordTypes?.map((rt) =>
      createRecordTypeIdentifier({
        recordTypeName: rt.name,
        funnelId: '',
        objectName: automation.objectName,
      }),
    ) ?? [];

  return uniquePrimitives([funnelRecordTypeName, ...rtNames].filter((rtName) => rtName !== ''));
};

export const getDocumentationStepsFromAutomation = (
  automation: AutomationStructureNew,
  propToUse: '_stageId' | 'stageName',
): string[] => {
  const { automationDetails } = automation;
  if (automationDetails.triggerType === AutomationTypes.FieldUpdate) {
    return [];
  }

  const steps = [
    automationDetails.automationParams.stageEnter?.toStage?.[propToUse],
    automationDetails.automationParams.stageExit?.toStage?.[propToUse],
    automationDetails.automationParams.stageExit?.fromStage?.[propToUse],
    automationDetails.automationParams.timeInStep?.stage?.[propToUse],
  ].filter((step) => !!step);

  const filteredSteps = steps.filter((nameOrId) =>
    !!nameOrId ? nameOrId !== ANY_STEP_NAME : true,
  );
  return filteredSteps as string[];
};

export const createConfigurationItemFromAutomation = (
  automation?: AutomationStructureNew,
  sweepUsers?: UserInfo[],
):
  | SalesforceItemSweepAutomation
  | SalesforceItemSweepAlert
  | SalesforceItemSweepAssignment
  | SalesforceItemSweepScheduledAssignment
  | SalesforceItemSweepDedupe
  | SalesforceItemSweepMatching
  | undefined => {
  const stageIds = automation ? getDocumentationStepsFromAutomation(automation, '_stageId') : [];
  const recordTypeNames = getRecordTypeNamesFromAutomation(automation);

  const updatedBy = sweepUsers?.find((user) => user.id === automation?.updatedById);
  const createdBy = sweepUsers?.find((user) => user.id === automation?.createdById);

  const conditionsCounter = automation?.automationDetails.when?.criteria?.criteria.length;
  const conditionsStr = conditionsCounter
    ? `\n(${conditionsCounter} ${pluralize('Condition')})`
    : '';

  const { actionText, triggerText } = automation ? getDisplayLineTexts(automation, true) : {};
  let _actionText = '';

  const components: string[] = [];

  actionText?.forEach((action) => {
    if (typeof action === 'string') {
      _actionText = _actionText === '' ? action : _actionText + ',\n' + action;
    } else {
      if (Array.isArray(action)) {
        components.push(...action.map((a) => a.toString()).flat());
      } else {
        components.push(action as string);
      }
    }
  });

  components.push(_actionText);

  const isAutomationDedupeAndMatching =
    automation?.type === AutomationType.Dedupe || automation?.type === AutomationType.Matching;

  return automation
    ? {
        id: automation.automationId,
        name: automation.name,
        label: automation.name,

        type: automation.type,
        createdAt: automation.createdAt ?? '',
        createdBy: createdBy?.name ?? 'No data',
        updatedAt: automation.updatedAt ?? '',
        updatedBy: updatedBy?.name ?? 'No data',
        objectApiNames: [automation.objectName],
        stagesNames: [], //TODO
        usedByLeadingFieldPicklist: stageIds,
        usedByRecordTypes: recordTypeNames,
        isActive: automation.isActive,
        trigger: triggerText + conditionsStr,
        actions: isAutomationDedupeAndMatching
          ? automation.automationDetails.actions.map(
              (action) =>
                (action.actionParams as DeduplicationActionParams | MatchingActionParams)
                  .dmType as string,
            )
          : automation.automationDetails.actions.map(
              ({ actionType }) => actionType as AutomationActionType,
            ),
        actionsToRender: components,
        metadataId: automation.automationId,
        parentType: automationTypeToConfigurationType(automation.type) ?? SWEEP_AUTOMATION,
      }
    : undefined;
};

export const getObjectNameColor = (objectName: string) => {
  const leadingColor = getObjectTypeColor(objectName)?.leadingColorName as keyof typeof colors;
  return (colors[leadingColor] as any)?.[100] ?? colors['blue'][100];
};

export const getConfigurationItemColor = (
  configurationItem: SalesforceItem,
  canvasColor?: string,
  tags?: Tag[],
): string => {
  // Canvas color takes precedence (but ignore white as it's the default)
  if (canvasColor && canvasColor.toLowerCase() !== '#ffffff') {
    return canvasColor;
  }

  // For tags, use the tag's own color
  if (configurationItem.parentType === UnclassifiedTypes.tag && tags) {
    const tag = tags.find((t) => t.id === configurationItem.id);
    if (tag?.color) {
      return (
        (colors[tag.color as keyof typeof colors] as any)?.[100] ?? tag.color ?? colors.blue[100]
      );
    }
  }

  // Default: derive from object name
  const objectName = configurationItem.objectApiNames?.[0];
  return getObjectNameColor(objectName) ?? colors.blue[100];
};
